import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Card,
  CardTitle,
  Badge,
  FormGroup,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap";
import {
  REQUESTLIST_URL,
  AGENCY_DOCUMENT_DELETE_URL,
  AGENCY_DOCUMENT_UPDATE_URL,
} from "../../utils/APIEndpoints";
import {
  authHeader,
  loggedUserDetails,
} from "../../utils/functions/CoreFunctions";
import API from "../../redux/actions/API";
import {
  dateTimeFormat,
  SingleStringFirstLetterCaps,
  FloatFixedFormat,
  getConfirm,
  getAlertToast,
} from "../../utils/functions/CustomFunctions";
import CustomViewButton from "../common/CustomViewButton";
import CustomEditButton from "../common/CustomEditButton";
import CustomDeleteButton from "../common/CustomDeleteButton";
import CustomTableLoader from "../common/CustomTableLoader";
import CustomTableEmptyRecord from "../common/CustomTableEmptyRecord";
import CustomTableHeader from "../common/CustomTableHeader";
import { Switch, Link } from "react-router-dom";
import { UPLOADDOCUMENT_PATH } from "../../utils/AppRoutes";
import Swal from "sweetalert2";
import Pagination from "../common/Pagination";
import { debounce } from "underscore";
//Table Columns
const tableColumns = [
  { title: "Name" },
  { title: "Document URL" },
  { title: "Payment Type" },
  { title: "Document Type" },
  { title: "Status" },
  { title: "Document Fee" },
  { title: "Created Date" },
  { title: "Active" },
  { title: "Actions" },
];

function Documents(props) {
  const { agencyId } = loggedUserDetails();
  //State
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const [filter, setFilter] = useState({
    serviceName: "",
    status: "",
    count: 10,
  });
  //Debounce
  const delayedQuery = useRef(
    debounce(
      (filter) =>
        getGridData({
          ...filter,
          // count: rowsPerPage || undefined,
          offset: currentPage,
          agencyId: parseInt(agencyId),
          isDeleted: false,
        }),
      500
    )
  ).current;

  //Onchange filter
  const changeFilterField = ({ target: { name, value } }) => {
    setFilter({ ...filter, [name]: value });
  };

  const handleReset = () => {
    setFilter((val) => ({
      ...val,
      serviceName: "",
      status: "",
    }));
  };

  const onPageChanged = (page) => {
    setCurrentPage(page - 1);
    getGridData({
      ...filter,
      // count: rowsPerPage || undefined,
      offset: page - 1,
      agencyId: parseInt(agencyId),
      isDeleted: false,
    });
  };

  useEffect(() => {
    delayedQuery(filter);
  }, [filter]);

  //Get Grid Data
  const getGridData = async (queryParams) => {
    try {
      setLoading(true);
      const options = {
        method: "GET",
        url: REQUESTLIST_URL,
        headers: authHeader(),
        params: queryParams,
      };
      const response = await API(options);
      const { status, data: initialResponse = [], totalCount } = response.data;
      if (status === "Success") {
        setTotalRecords(totalCount);
        let dataToDis = initialResponse.map((record) => {
          let { serviceCreatedDate, serviceName, serviceAmount } = record;
          let formattedData = {
            serviceCreatedDate: serviceCreatedDate
              ? dateTimeFormat(serviceCreatedDate, "MM/DD/YYYY hh:mm A")
              : "",
            serviceName: SingleStringFirstLetterCaps(serviceName),
            serviceAmount: FloatFixedFormat(serviceAmount),
          };
          let dataToReturn = { ...record, ...formattedData };
          return dataToReturn;
        });
        setGridData(dataToDis);
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast("error", data.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowPerPage = ({ target: { value, name } }) => {
    setCurrentPage(0);
    setFilter((va) => ({ ...va, [name]: value || undefined }));
  };

  const removeRecord = (event) => {
    const agencyServiceId = event.currentTarget.dataset.agency_service_id;
    Swal.fire(
      getConfirm("warning", "Are you sure you want to remove this document?")
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const options = {
            method: "DELETE",
            url: AGENCY_DOCUMENT_DELETE_URL,
            headers: authHeader(),
            params: { agencyServiceId },
          };
          const response = await API(options);
          const { status } = response.data;
          if (status === "Success") {
            getGridData({
              ...filter,
              // count: rowsPerPage || undefined,
              offset: currentPage,
              //agencyId: parseInt(agencyId),
              isDeleted: false,
            });
            Swal.fire(getAlertToast("success", "Record removed successfully"));
          }
        } catch (error) {
          if (error.response) {
            let { data } = error.response;
            Swal.fire(getAlertToast("error", data.message));
          }
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const statusChange = async (e) => {
    const id = e.currentTarget.dataset.uniqueid;
    const { checked } = e.target;
    setLoading(true);
    const options = {
      method: "PUT",
      url: AGENCY_DOCUMENT_UPDATE_URL,
      headers: authHeader(),
      params: {
        agencyId: parseInt(agencyId),
      },
      data: {
        agencyServiceId: parseInt(id),
        isActive: checked,
      },
    };
    try {
      const response = await API(options);
      const { status, message } = response.data;
      if (status === "Success") {
        Swal.fire(getAlertToast("success", message));
        getGridData({
          ...filter,
          // count: rowsPerPage || undefined,
          offset: currentPage,
          agencyId: parseInt(agencyId),
          isDeleted: false,
        });
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast("error", data.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const { serviceName, status } = filter;

  return (
    <div>
      <Switch>
        <Card body className="mt-5">
          <div>
            <CardTitle className="mb-4 f-l w-fit ">Documents</CardTitle>
            <i
              className="fas fa-filter"
              style={{
                float: "right",
                margin: "10px 7px 3px 16px",
                fontSize: "19px",
                cursor: "pointer",
              }}
              onClick={filterToggle}
            ></i>
            <Link to={UPLOADDOCUMENT_PATH}>
              <Button className="f-r" color="primary">
                <i className="fas fa-file-medical"></i>New Document
              </Button>
            </Link>
          </div>
          {isFilterOpen && (
            <Row form className="mb-4">
              <Col md={3}>
                <FormGroup>
                  <Input
                    type="text"
                    name="serviceName"
                    placeholder="Document Name"
                    onChange={changeFilterField}
                    value={serviceName}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Input
                    type="select"
                    name="status"
                    placeholder="Status"
                    onChange={changeFilterField}
                    value={status}
                  >
                    <option value={""}>-- Show All Status --</option>
                    <option value={"New"}>New</option>
                    <option value={"Pending"}>Pending</option>
                    <option value={"Accepted"}>Accepted</option>
                    <option value={"Rejected"}>Rejected</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <div style={{ marginTop: "4px" }}>
                  <Button color="danger" onClick={handleReset}>
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          )}
          <Table responsive hover>
            {/* Table Header */}
            <CustomTableHeader tableColumns={tableColumns} />
            {/* Table Header */}

            {/* Table Body */}
            <tbody>
              {loading ? (
                // Table Loader
                <CustomTableLoader columnsCount={tableColumns.length} />
              ) : (
                // Table Loader
                <Fragment>
                  {!gridData.length ? (
                    //  Empty Record
                    <CustomTableEmptyRecord
                      columnsCount={tableColumns.length}
                    />
                  ) : (
                    //  Empty Record
                    <Fragment>
                      {gridData.map((record, key) => {
                        const {
                          serviceName,
                          serviceDomainURL,
                          paymentType,
                          documentType,
                          serviceAmount,
                          serviceCreatedDate,
                          agencyServiceId,
                          isActive,
                        } = record;

                        return (
                          <tr key={`docs_list_${key}`}>
                            <td>{serviceName}</td>
                            <td>
                              <a
                              className="link"
                                target="_blank"
                                href={
                                  window.location.origin +
                                  "/preview-document/" +
                                  serviceDomainURL
                                }
                              >
                                /{serviceDomainURL}
                              </a>
                            </td>
                            <td>{paymentType}‎</td>
                            <td>
                              {documentType === "Pdf" ? "PDF" : "WebForm"}
                            </td>
                            <td>
                              {record.status === "Pending" ? (
                                <Badge color="warning">{record.status}</Badge>
                              ) : record.status === "Accepted" ? (
                                <Badge color="success">{record.status}</Badge>
                              ) : record.status === "New" ? (
                                <Badge color="primary">{record.status}</Badge>
                              ) : (
                                <Badge color="danger">{record.status}</Badge>
                              )}
                            </td>
                            <td>
                              {parseInt(serviceAmount) > 0
                                ? `$ ${serviceAmount}`
                                : "-"}{" "}
                              ‎
                            </td>
                            <td>{serviceCreatedDate}</td>
                            <td>
                              {record.status !== "New" ? (
                                <div className="form-check form-switch">
                                  <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={agencyServiceId}
                                    name="customSwitch"
                                    checked={isActive}
                                    data-uniqueid={agencyServiceId}
                                    onChange={statusChange}
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={agencyServiceId}
                                  >
                                    
                                  </Label>
                                </div>
                              ) : (
                                ""
                              )}
                            </td>
                            <td>
                              {/* Custom View Button  */}
                              <CustomViewButton
                                keyvalue={key}
                                tag={Link}
                                target="_blank"
                                to={`preview-document/${serviceDomainURL}`}
                              />
                              {/* Custom View Button  */}

                              {/* Custom Edit Button  */}
                              {(record.status === "New" ||
                                isActive === false) && (
                                <CustomEditButton
                                  keyvalue={key}
                                  tag={Link}
                                  to={`edit-document/${agencyServiceId}`}
                                />
                              )}
                              {/* Custom Edit Button  */}

                              {/* Custom Remove Button  */}
                              {record.status === "New" && (
                                <CustomDeleteButton
                                  keyvalue={key}
                                  data-agency_service_id={agencyServiceId}
                                  onClick={removeRecord}
                                />
                              )}
                              {/* Custom Remove Button  */}
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  )}
                </Fragment>
              )}
            </tbody>
          </Table>
          {gridData.length ? (
            <div className="d-flex py-4 justify-content-end space_between">
              <div className="space_between">
                <div>
                  <p className="per_page">Records/Page</p>
                </div>
                <div>
                  <FormGroup>
                    <Input
                      className="pagi_drop"
                      type="select"
                      name="count"
                      onChange={handleRowPerPage}
                      value={filter.count}
                    >
                      <option value={""}>Show All</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Input>
                  </FormGroup>
                </div>
              </div>
              {filter.count && (
                <div>
                  <Pagination
                    itemClass="page-item"
                    linkClass="page-link"
                    activePage={currentPage + 1}
                    itemsCountPerPage={filter.count}
                    totalItemsCount={totalRecords}
                    onChange={onPageChanged}
                  />
                </div>
              )}
            </div>
          ) : (
            ""
          )}
        </Card>
      </Switch>
    </div>
  );
}

export default Documents;
